<template>
  <div class="referrer-content">
    <loading v-model:active="isLoading" :is-full-page="true" />

    <div class="header-section">
      <div class="title">
        <span>Referral Clinician</span>
        <p>Please select a referral clinician or practice</p>
      </div>
    </div>

    <div class="filter-section">
      <div class="filter-group">
        <div class="search-wrapper">
          <input 
            type="text" 
            v-model="searchPostcode" 
            placeholder="Search postcode..."
            class="search-input"
            @input="handlePostcodeInput"
          />
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24">
            <path fill="#013B82" d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <div v-if="postcodeError" class="error-message">{{ postcodeError }}</div>
        </div>

        <div class="search-wrapper">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search referrers..."
            class="search-input"
          />
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24">
            <path fill="#013B82" d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
        </div>
        
        <div class="filters-right">
          <div class="specialty-filter-group">
            <div class="filter-label">Specialties:</div>
            <div class="specialty-buttons">
              <button
                v-for="specialty in specialtyOptions"
                :key="specialty"
                class="specialty-filter"
                :class="{ active: selectedSpecialties.includes(specialty) }"
                @click="toggleSpecialty(specialty)"
              >
                {{ specialty }}
              </button>
            </div>
          </div>
          <button class="clear-filters" @click="clearFilters">
            Clear All
          </button>
        </div>
      </div>
    </div>

    <div class="screening-questions__list">
      <template v-if="filteredReferrers.length">
        <div 
          v-for="(referrer, index) in filteredReferrers" 
          :key="index" 
          class="screening-question" 
          :class="{
            'referrer-selected': selectedReferrer === referrer.id,
            'referrer-self': referrer.id === null
          }" 
          @click="selectReferrer(referrer.id)"
        >
          <div class="screening-question-group">
            <div class="question">
              <div class="name">{{ referrer.name }}</div>
              <div class="location">{{ referrer.experience }}</div>
              <div class="location" v-if="referrer.location">{{ referrer.location }}</div>
              <div class="distance-info" v-if="referrer.distance != null && referrer.distance > 0">
                <svg class="distance-icon" width="16" height="16" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                {{ referrer.distance.toFixed(2) }} miles
              </div>
              <div class="specialties" v-if="referrer.specialties?.length">
                <div 
                  class="specialty-tag" 
                  v-for="(specialty, sIndex) in referrer.specialties" 
                  :key="sIndex"
                >
                  {{ specialty }}
                </div>
              </div>
            </div>
            <button class="btn" :class="{ 'btn-positive': selectedReferrer === referrer.id }">
              <svg v-if="selectedReferrer === referrer.id" width="24" height="24" viewBox="0 0 24 24">
                <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span v-else>&nbsp;</span>
            </button>
          </div>
        </div>
      </template>

      <div v-else class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" class="empty-icon">
          <path fill="#013B82" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <h3>No matching referrers found</h3>
        <p>Try adjusting your search or filters</p>
        <button class="clear-filters" @click="clearFilters">
          Clear All Filters
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import authStore from "../../../../store/auth-store";
import postcodeService from '../../../../services/postcode-service.ts';
import { debounce } from 'lodash';

export default {
  props: {
    patientPostcode: {
      type: String,
      default: ''
    },
    selectedReferrerId: {
      type: [String, Number, null],
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      referrers: [],
      searchPostcode: '',
      searchQuery: '',
      selectedReferrer: null,
      postcodeError: '',
      specialtyOptions: [
        "Endodontics",
        "Periodontics",
        "Prosthodontics",
        "Restorative"
      ],
      selectedSpecialties: []
    }
  },
  computed: {
    filteredReferrers() {
      let filtered = this.referrers.filter(referrer => {
        const matchesSearch = !this.searchQuery ||
          referrer.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (referrer.location && referrer.location.toLowerCase().includes(this.searchQuery.toLowerCase()));

        const matchesSpecialties = this.selectedSpecialties.length === 0 ||
          (referrer.specialties &&
            this.selectedSpecialties.some(spec => referrer.specialties.includes(spec)));

        return matchesSearch && matchesSpecialties;
      });

      // Sort by distance if postcode is provided
      if (this.searchPostcode && filtered.some(r => r.distance !== undefined)) {
        filtered.sort((a, b) => {
          if (a.distance === undefined) return 1;
          if (b.distance === undefined) return -1;
          return a.distance - b.distance;
        });
      }

      return filtered;
    },
    user() {
      return authStore.state.user;
    },
  },
  async mounted() {
    this.referrers = await this.getReferrers();
  },
  methods: {
    clearSelection() {
      this.selectedReferrer = null;
    },
    selectReferrer(id) {
      this.selectedReferrer = id;
      const selectedReferrerObj = this.referrers.find(referrer => referrer.id === id);
      const referrerName = selectedReferrerObj ? selectedReferrerObj.name : '';
      this.$emit('referrer-selected', id, referrerName);
    },
    async getReferrers() {
      this.isLoading = true;
      try {
        const response = await this.axios.get("/api/survey/getReferrers");
        const referrers = response.data;
        // if (this.user != null) {
        //   referrers.unshift({ id: this.user.id, name: "Refer to self", location: "", specialties: [], distance: 0.000001 });
        // }
        return referrers;
      } catch (error) {
        console.error('Error fetching referrers:', error);
        return [];
      } finally {
        this.isLoading = false;
      }
    },
    toggleSpecialty(specialty) {
      if (this.selectedSpecialties.includes(specialty)) {
        this.selectedSpecialties = this.selectedSpecialties.filter(s => s !== specialty);
      } else {
        this.selectedSpecialties = [...this.selectedSpecialties, specialty];
      }
    },
    clearFilters() {
      this.selectedSpecialties = [];
      this.searchQuery = '';
      this.searchPostcode = '';
      this.postcodeError = '';
      this.referrers = this.referrers.map(r => ({
        ...r,
        distance: undefined
      }));
    },

    handlePostcodeInput: debounce(async function() {
      if (!this.searchPostcode) {
        this.postcodeError = '';
        this.referrers = this.referrers.map(r => ({
          ...r,
          distance: undefined
        }));
        return;
      }

      try {
        const validation = await postcodeService.validatePostcode(this.searchPostcode);
        if (!validation.isValid) {
          this.postcodeError = 'Invalid postcode';
          return;
        }

        this.postcodeError = '';
        await this.updateDistances(validation.postcode);
      } catch (error) {
        this.postcodeError = 'Error validating postcode';
        console.error('Postcode validation error:', error);
      }
    }, 1000),

    async updateDistances(fromPostcode) {
      this.isLoading = true;
      try {
        const updatedReferrers = await Promise.all(
          this.referrers.map(async (referrer) => {
            if (!referrer.location || referrer.id === null) {
              return { ...referrer, distance: undefined };
            }

            try {
              const result = await postcodeService.getDistanceBetweenPostcodes(
                fromPostcode,
                referrer.location
              );
              return {
                ...referrer,
                distance: result.distance === 0 ? 0.01 : result.distance
              };
            } catch (error) {
              console.error(`Error calculating distance for ${referrer.name}:`, error);
              return { ...referrer, distance: undefined };
            }
          })
        );

        this.referrers = updatedReferrers;
      } catch (error) {
        console.error('Error updating distances:', error);
      } finally {
        this.isLoading = false;
      }
    }
  },
  components: {
    Loading
  },
  watch: {
    patientPostcode: {
      immediate: true,
      handler(newPostcode) {
        if (newPostcode && newPostcode.trim()) {
          this.searchPostcode = newPostcode.trim();
          this.handlePostcodeInput();
        }
      }
    },
    selectedReferrerId(newVal) {
      this.selectedReferrer = newVal;
    }
  },
}
</script>

<style lang="scss" scoped>
.referrer-content {
  margin-bottom: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.header-section {
  margin-bottom: 24px;

  .title {
    padding: 14px 18px;
    background: linear-gradient(135deg, #013B82, #0A2D5C);
    color: #FFF;
    font-weight: 600;
    margin-bottom: 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(1, 59, 130, 0.15);
    letter-spacing: 0.5px;
  }

  .sub-title {
    padding: 14px 18px;
    background: #f5f8fd;
    color: #0A2D5C;
    font-weight: 600;
    margin-bottom: 18px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    border-radius: 10px;
    border: 1px solid rgba(1, 59, 130, 0.1);

    p {
      font-size: 14px;
      font-weight: normal;
      margin-top: 10px;
      color: #000001;
    }
  }
}

.filter-section {
  margin-bottom: 24px;
  padding: 18px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .filter-group {
    display: flex;
    gap: 20px;
    align-items: center;
    flex-wrap: wrap;
  }

  .search-wrapper {
    position: relative;
    flex: 1;
    min-width: 250px;

    .search-input {
      width: 100%;
      padding: 12px 16px 12px 42px;
      border: 1px solid #D1D5DB;
      border-radius: 30px;
      font-size: 14px;
      transition: all 0.3s ease;
      background-color: white;

      &:focus {
        border-color: #013B82;
        box-shadow: 0 0 0 3px rgba(1, 59, 130, 0.1);
        outline: none;
      }
    }

    .search-icon {
      position: absolute;
      left: 15px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
    }

    .error-message {
      color: #EF4444;
      font-size: 13px;
      margin-top: 5px;
      margin-left: 15px;
    }
  }

  .filters-right {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
  }

  .specialty-filter-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .filter-label {
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
  }

  .specialty-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .specialty-filter {
    padding: 8px 14px;
    border: 1px solid #D1D5DB;
    border-radius: 20px;
    background: white;
    color: #374151;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      border-color: #013B82;
      color: #013B82;
      box-shadow: 0 1px 3px rgba(1, 59, 130, 0.1);
    }

    &.active {
      background: linear-gradient(135deg, #013B82, #0A2D5C);
      border-color: #013B82;
      color: white;
      box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);
    }
  }

  .clear-filters {
    padding: 8px 16px;
    background: transparent;
    border: 1px solid #D1D5DB;
    border-radius: 20px;
    color: #6B7280;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      border-color: #013B82;
      color: #013B82;
      background-color: rgba(1, 59, 130, 0.05);
    }
  }
}

.screening-questions__list {
  display: grid;
  gap: 12px;

  .screening-question {
    border: 1px solid #E5E7EB;
    border-radius: 10px;
    transition: all 0.3s ease;
    background: white;
    cursor: pointer;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      border-color: #D1D5DB;
    }

    &.referrer-selected {
      border-color: #013B82;
      border-left: 4px solid #013B82;
      background-color: rgba(1, 59, 130, 0.03);
      box-shadow: 0 2px 10px rgba(1, 59, 130, 0.1);
    }

    &.referrer-self {
      border-color: #013B82;
      background-color: rgba(1, 59, 130, 0.03);
      box-shadow: 0 2px 10px rgba(1, 59, 130, 0.08);

      .name {
        font-weight: 700;
        color: #013B82;
      }
    }

    .screening-question-group {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 18px;

      .question {
        flex: 1;
        .name {
          font-weight: 600;
          color: #111827;
          margin-bottom: 8px;
          font-size: 16px;
        }

        .location {
          color: #000001;
          font-size: 0.9em;
          margin-top: 8px;
        }

        .distance-info {
          align-items: center;
          color: #013B82;
          font-size: 0.9em;
          margin-top: 10px;
          font-weight: 500;
          
          .distance-icon {
            margin-right: 5px;
            color: #013B82;
          }
        }

        .specialties {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 15px;

          .specialty-tag {
            background-color: rgba(1, 59, 130, 0.08);
            color: #013B82;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 0.8em;
            font-weight: 500;
            border: 1px solid rgba(1, 59, 130, 0.12);
            transition: all 0.2s ease;

            &:hover {
              background-color: rgba(1, 59, 130, 0.12);
            }
          }
        }
      }

      .btn {
        width: 56px;
        height: 56px;
        border: 2px solid #D1D5DB;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
        transition: all 0.3s ease;
        padding: 0;
        margin-left: 16px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        svg {
          color: #013B82;
          transition: all 0.3s ease;
        }

        &.btn-positive {
          background: linear-gradient(135deg, #013B82, #0A2D5C);
          border-color: #013B82;
          box-shadow: 0 3px 8px rgba(1, 59, 130, 0.2);

          svg {
            color: white;
          }
        }
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    background: white;
    border-radius: 10px;
    border: 1px solid #E5E7EB;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .empty-icon {
      margin-bottom: 20px;
      opacity: 0.8;
    }

    h3 {
      color: #111827;
      margin-bottom: 8px;
      font-size: 18px;
      font-weight: 600;
    }

    p {
      color: #6B7280;
      margin-bottom: 20px;
    }

    .clear-filters {
      padding: 10px 18px;
      background-color: #013B82;
      color: white;
      border: none;
      border-radius: 8px;
      font-weight: 500;
      transition: all 0.2s ease;
      
      &:hover {
        background-color: #0A2D5C;
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(1, 59, 130, 0.2);
      }
    }
  }
}

.referrer-self {
  .specialties {
    display: none !important;
  }
}

@media (max-width: 768px) {
  .filter-section {
    .filter-group {
      gap: 15px;
      flex-direction: column;
      align-items: stretch;
    }

    .search-wrapper {
      min-width: 100%;
    }

    .filters-right {
      width: 100%;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      gap: 15px;
    }

    .specialty-filter-group {
      flex-direction: column;
      align-items: center;
      gap: 12px;
      width: 100%;
    }

    .filter-label {
      text-align: center;
      margin-bottom: 0;
    }

    .specialty-buttons {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      width: 100%;
    }
  }

  .screening-questions__list {
    .screening-question {
      .screening-question-group {
        padding: 14px !important;

        .btn {
          width: 46px !important;
          height: 46px !important;
          margin-left: 12px;
        }
      }
    }
  }

  .specialty-filter {
    padding: 8px 14px !important;
    font-size: 13px !important;
  }

  .clear-filters {
    padding: 8px 16px !important;
    font-size: 13px !important;
    align-self: center;
  }
}
</style>